import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { format, addDays, subDays, isSameDay } from 'date-fns';
import { GET_SOCIAL_PROFILES, GET_POSTS } from '../../graphql';
import { Header } from '../nav/Header';
import { useProfileSelection } from '../../context/ProfileSelectionContext';
import { useAccount } from '../../context/AccountContext';
import {
  SimplifiedPostForm,
  SimplifiedPostFormData,
  convertToSimplifiedFormData,
} from '../posts/SimplifiedPostForm';
import { TopicsSection } from '../topics/TopicsSection';
import { CalendarDayView } from '../posts/CalendarDayView';
import { OnboardingModal } from '../account/OnboardingModal';
import { ActiveProjectsSection } from '../projects/ActiveProjectsSection';
import { MetricsBar } from '../dashboard/MetricsBar';
import { SocialPost, Topic, TimeSlot, OrganizedPosts, MediaType, Platform } from '../../types';
import { GetSocialProfilesResponse, GetSocialPostsResponse } from '../../types/graphql';

// Helper function for converting timestamps
const toLocalTime = (unixTimestamp: string | number): Date => {
  const timestamp = typeof unixTimestamp === 'string' ? parseInt(unixTimestamp) : unixTimestamp;
  return new Date(timestamp.toString().length > 10 ? timestamp : timestamp * 1000);
};

// Helper function for rounding to nearest slot
const roundToNearestSlot = (date: Date): string => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const totalMinutes = hours * 60 + minutes;
  const roundedMinutes = Math.floor(totalMinutes / 15) * 15;
  const roundedHours = Math.floor(roundedMinutes / 60);
  const remainingMinutes = roundedMinutes % 60;
  return `${String(roundedHours).padStart(2, '0')}:${String(remainingMinutes).padStart(2, '0')}`;
};

export function Dashboard() {
  const [editMode, setEditMode] = useState<boolean>(false);
  const [selectedPost, setSelectedPost] = useState<SocialPost | null>(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform | ''>('');
  const [mediaFilters, setMediaFilters] = useState<MediaType[]>([
    'IMAGE',
    'VIDEO',
    'GALLERY',
    'PHOTO',
    'BOOKMARK',
  ]);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [formData, setFormData] = useState<SimplifiedPostFormData>({
    message: '',
    postType: '',
    mediaUrl: '',
    linkUrl: '',
    scheduleTime: new Date(),
    isStory: false,
    notes: '',
    isDraft: false,
  });
  const { selectedAccount, setSelectedAccount, selectedProfiles, setSelectedProfiles } =
    useProfileSelection();
  const { accounts, isLoading: accountsLoading, refetchAccounts } = useAccount();
  const [selectedFormProfiles, setSelectedFormProfiles] = useState<Set<string>>(new Set());
  const [showPostFormModal, setShowPostFormModal] = useState<boolean>(false);
  const [topicsRefetch, setTopicsRefetch] = useState<(() => Promise<any>) | null>(null);
  const [showOnboarding, setShowOnboarding] = useState<boolean>(false);
  const [onboardingInitialStep, setOnboardingInitialStep] = useState<number>(1);
  const [onboardingAccountId, setOnboardingAccountId] = useState<string | null>(null);
  const scrollToCurrentTimeRef = useRef<(() => void) | null>(null);
  const shouldScrollAfterDateChangeRef = useRef<boolean>(false);

  // Onboarding checks the currently selected account's social profiles.
  const onboardingAccountIdForCheck = selectedAccount?.id;
  const selectedAccountPlan = selectedAccount?.plan
    ? selectedAccount.plan.charAt(0).toUpperCase() + selectedAccount.plan.slice(1)
    : '';
  const {
    data: selectedAccountSocialProfiles,
    loading: selectedAccountSocialProfilesLoading,
    refetch: refetchSelectedAccountSocialProfiles,
  } = useQuery<GetSocialProfilesResponse>(GET_SOCIAL_PROFILES, {
    variables: { accountId: onboardingAccountIdForCheck },
    skip: !onboardingAccountIdForCheck,
  });

  const { data: socialProfilesData } = useQuery<GetSocialProfilesResponse>(GET_SOCIAL_PROFILES, {
    variables: { accountId: selectedAccount?.id },
    skip: !selectedAccount?.id,
  });

  const { data: postsData, refetch: refetchPosts } = useQuery<GetSocialPostsResponse>(GET_POSTS, {
    variables: {
      socialProfileIds: Array.from(selectedProfiles),
    },
    skip: selectedProfiles.size === 0,
  });

  useEffect(() => {
    if (socialProfilesData?.socialProfiles && socialProfilesData.socialProfiles.length > 0) {
      const availablePlatforms = socialProfilesData.socialProfiles
        .filter((profile) => profile.isActive)
        .map((profile) => profile.platform) as Platform[];

      if (!availablePlatforms.includes(selectedPlatforms as Platform)) {
        setSelectedPlatforms(availablePlatforms[0]);
        setFormData((prev) => ({
          ...prev,
          platform: availablePlatforms[0],
        }));
      }
    }
  }, [socialProfilesData, selectedAccount, selectedPlatforms]);

  // Update form profiles when selected profiles or platform changes
  useEffect(() => {
    if (socialProfilesData?.socialProfiles) {
      const activeProfilesForPlatform = socialProfilesData.socialProfiles
        .filter((profile) => profile.platform === selectedPlatforms && profile.isActive)
        .map((profile) => profile.id);

      // Only auto-select platform profiles if no profiles are selected at all
      // This preserves user's manual selections across layouts
      if (selectedProfiles.size === 0 && activeProfilesForPlatform.length > 0) {
        setSelectedProfiles(new Set(activeProfilesForPlatform));
      }

      if (activeProfilesForPlatform.length === 1) {
        setSelectedFormProfiles(new Set(activeProfilesForPlatform));
      } else {
        setSelectedFormProfiles(new Set());
      }
    }
  }, [socialProfilesData, selectedPlatforms, selectedProfiles, setSelectedProfiles]);

  useEffect(() => {
    if (selectedAccount !== null) {
      const timer = setTimeout(() => {}, 1000);
      return () => clearTimeout(timer);
    }
    const fallbackTimer = setTimeout(() => {}, 5000);
    return () => clearTimeout(fallbackTimer);
  }, [selectedAccount]);

  // Check if user has no accounts or no social profiles and show onboarding
  useEffect(() => {
    if (accountsLoading) return;

    const hasAccounts = accounts.length > 0;

    if (!hasAccounts) {
      setOnboardingInitialStep(1);
      setOnboardingAccountId(null);
      setShowOnboarding(true);
    } else if (!selectedAccountSocialProfilesLoading && selectedAccountSocialProfiles) {
      const hasSocialProfiles =
        selectedAccountSocialProfiles.socialProfiles &&
        selectedAccountSocialProfiles.socialProfiles.length > 0;

      if (!hasSocialProfiles) {
        setOnboardingInitialStep(2);
        setOnboardingAccountId(onboardingAccountIdForCheck || null);
        setShowOnboarding(true);
      } else {
        setShowOnboarding(false);
      }
    }
  }, [
    accounts,
    accountsLoading,
    selectedAccountSocialProfiles,
    selectedAccountSocialProfilesLoading,
    onboardingAccountIdForCheck,
  ]);

  // Calendar day view setup - no need for date range or days array

  const timeSlots = useMemo((): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);

    for (let i = 0; i < 96; i++) {
      const hour = Math.floor(i / 4);
      const minutes = (i % 4) * 15;
      const slot = new Date(startOfDay);
      slot.setHours(hour, minutes, 0, 0);
      slots.push({
        date: slot,
        key: format(slot, 'HH:mm'),
        label: format(slot, 'h:mm a'),
      });
    }
    return slots;
  }, []);

  const organizedPosts = useMemo((): OrganizedPosts => {
    if (!postsData?.socialPosts) return {};

    const organized: OrganizedPosts = postsData.socialPosts.reduce((acc, post) => {
      const localDate = toLocalTime(post.scheduleTime);
      const date = format(localDate, 'yyyy-MM-dd');
      const roundedTime = roundToNearestSlot(localDate);

      if (!acc[date]) acc[date] = {};
      if (!acc[date][roundedTime]) acc[date][roundedTime] = [];

      acc[date][roundedTime].push({
        ...post,
        platform: post.platform?.toLowerCase() as Platform,
        localDate,
      });

      return acc;
    }, {} as OrganizedPosts);

    return organized;
  }, [postsData]);

  // Handle scrolling to current time after date changes to today
  useEffect(() => {
    if (shouldScrollAfterDateChangeRef.current) {
      const today = new Date();
      const isViewingToday = isSameDay(currentDate, today);

      if (isViewingToday) {
        // Small delay to ensure CalendarDayView has rendered with the new date
        const timer = setTimeout(() => {
          if (scrollToCurrentTimeRef.current) {
            scrollToCurrentTimeRef.current();
          }
          shouldScrollAfterDateChangeRef.current = false;
        }, 150); // Slightly longer delay to ensure DOM is ready

        return () => clearTimeout(timer);
      }
    }
  }, [currentDate]);

  const handlePreviousDay = () => {
    setCurrentDate(subDays(currentDate, 1));
  };

  const handleNextDay = () => {
    setCurrentDate(addDays(currentDate, 1));
  };

  const handleTopicSelect = (topic: Topic) => {
    setEditMode(false);
    setSelectedPost(null);

    // Initialize with profiles from selectedProfiles
    setSelectedFormProfiles(new Set(Array.from(selectedProfiles)));

    const postType =
      topic.mediaType === 'VIDEO'
        ? 'video'
        : topic.mediaType === 'BOOKMARK'
          ? 'link'
          : topic.mediaType === 'IMAGE' || topic.mediaType === 'PHOTO'
            ? 'photo'
            : 'link';

    setFormData({
      message: topic.summary || '',
      postType: postType as any,
      mediaUrl: topic.mediaUrl || '',
      linkUrl: topic.destinationUrl || '',
      scheduleTime: new Date(),
      isStory: false,
      notes: '',
      isDraft: false,
    });
    setShowPostFormModal(true);
  };

  const handleTopicDrop = (topic: Topic, scheduledDate: Date) => {
    setEditMode(false);
    setSelectedPost(null);

    // Initialize with profiles from selectedProfiles
    setSelectedFormProfiles(new Set(Array.from(selectedProfiles)));

    const postType =
      topic.mediaType === 'VIDEO'
        ? 'video'
        : topic.mediaType === 'BOOKMARK'
          ? 'link'
          : topic.mediaType === 'IMAGE' || topic.mediaType === 'PHOTO'
            ? 'photo'
            : 'link';

    setFormData({
      message: topic.summary || '',
      postType: postType as any,
      mediaUrl: topic.mediaUrl || '',
      linkUrl: topic.destinationUrl || '',
      scheduleTime: scheduledDate,
      isStory: false,
      notes: '',
      isDraft: false,
    });
    setShowPostFormModal(true);
  };

  const handleEditPost = (post: SocialPost) => {
    setSelectedPost(post);
    setEditMode(true);
    setFormData(
      convertToSimplifiedFormData({
        title: post.title || '',
        summary: post.summary || '',
        postUrl: post.postUrl || '',
        imageUrl: post.imageUrl || '',
        thoughts: post.thoughts || '',
        postType: post.postType || '',
        scheduleTime: toLocalTime(post.scheduleTime),
        notes: post.notes,
        isDraft: post.isDraft,
      })
    );
    // Initialize selectedFormProfiles with the post's profile
    if (post.socialProfileId) {
      setSelectedFormProfiles(new Set([post.socialProfileId]));
    } else {
      setSelectedFormProfiles(new Set());
    }
    setShowPostFormModal(true);
  };

  /*  const handlePlatformChange = (event: any, newPlatforms: Platform | Platform[]) => {
    const platforms = Array.isArray(newPlatforms) ? newPlatforms : [newPlatforms];
    if (platforms.length > 0) {
      setSelectedPlatforms(platforms[0]);
      setFormData((prev) => ({
        ...prev,
        platform: platforms[0],
      }));
    }
  };
*/
  const handleMediaFilterChange = (event: any, newFilters: MediaType[]) => {
    if (newFilters.length > 0) {
      setMediaFilters(newFilters);
    }
  };

  const handleCellClick = (scheduledDate: Date) => {
    setEditMode(false);
    setSelectedPost(null);

    // Initialize with profiles from selectedProfiles
    setSelectedFormProfiles(new Set(Array.from(selectedProfiles)));

    setFormData({
      message: '',
      postType: '',
      mediaUrl: '',
      linkUrl: '',
      scheduleTime: scheduledDate,
      isStory: false,
      notes: '',
      isDraft: false,
    });
    setShowPostFormModal(true);
  };

  const handleClosePostFormModal = () => {
    setShowPostFormModal(false);
    setEditMode(false);
    setSelectedPost(null);
    setSelectedFormProfiles(new Set());
    setFormData({
      message: '',
      postType: '',
      mediaUrl: '',
      linkUrl: '',
      scheduleTime: new Date(),
      isStory: false,
      notes: '',
      isDraft: false,
    });
  };

  const handleRefresh = async () => {
    try {
      // Refetch posts if we have selected profiles
      if (selectedProfiles.size > 0) {
        await refetchPosts();
      }
      // Refetch topics if refetch function is available and we have an account
      if (selectedAccount?.id && topicsRefetch && typeof topicsRefetch === 'function') {
        await topicsRefetch();
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  const handleOnboardingComplete = async () => {
    setShowOnboarding(false);
    try {
      await refetchAccounts();
      if (onboardingAccountIdForCheck) {
        await refetchSelectedAccountSocialProfiles();
      }
    } catch (error) {
      console.error('Error refetching data after onboarding:', error);
    }
  };

  const handleOnboardingSkip = () => {
    setShowOnboarding(false);
  };

  return (
    <>
      <div className="ink-page flex w-full flex-col h-full min-h-0 overflow-hidden">
        <div className="flex flex-col flex-1 overflow-y-auto md:overflow-hidden">
          <Header
            showPlatformToggle={true}
            selectedPlatforms={selectedPlatforms}
            selectedProfiles={selectedProfiles}
            onProfileToggle={setSelectedProfiles}
            selectedAccount={selectedAccount}
            onAccountChange={setSelectedAccount}
            onCreatePost={() => {
              setEditMode(false);
              setSelectedPost(null);
              setSelectedFormProfiles(new Set(Array.from(selectedProfiles)));
              setFormData({
                message: '',
                postType: '',
                mediaUrl: '',
                linkUrl: '',
                scheduleTime: new Date(),
                isStory: false,
                notes: '',
                isDraft: false,
              });
              setShowPostFormModal(true);
            }}
          />
          <MetricsBar publishedPosts={127} impressions={45230} reactions={3240} clicks={1890} />
          <div className="w-full max-w-full overflow-y-auto md:overflow-hidden relative flex-1 flex flex-col min-h-0">
            <div className="p-1 m-0 w-full flex-1 relative overflow-y-auto md:overflow-hidden box-border bg-primary-background flex flex-col min-h-0">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-1 transition-all duration-200 box-border w-full flex-1 min-h-0">
                <div className="col-span-12 md:col-span-4 flex flex-col min-h-0">
                  <div className="bg-core-dark p-6 h-full flex flex-col min-h-0 md:min-h-0 md:h-full h-[150px]">
                    <div className="flex-1 min-h-0 relative overflow-y-auto overflow-x-auto md:overflow-y-auto h-full">
                      <div className="relative md:absolute md:inset-0 w-full h-full">
                        <CalendarDayView
                          day={currentDate}
                          timeSlots={timeSlots}
                          posts={organizedPosts}
                          onTimeSlotDrop={handleTopicDrop}
                          onPostClick={handleEditPost}
                          onCellClick={handleCellClick}
                          selectedScheduleTime={formData.scheduleTime}
                          onScrollToCurrentTimeReady={(fn) => {
                            scrollToCurrentTimeRef.current = fn;
                          }}
                          onRefresh={handleRefresh}
                          onPreviousDay={handlePreviousDay}
                          onNextDay={handleNextDay}
                          onScrollToCurrentTime={() => {
                            const today = new Date();
                            const isViewingToday = isSameDay(currentDate, today);

                            if (!isViewingToday) {
                              // If not viewing today, navigate to today first
                              setCurrentDate(today);
                              // Set flag to scroll after date change
                              shouldScrollAfterDateChangeRef.current = true;
                            } else {
                              // Already viewing today, just scroll
                              if (scrollToCurrentTimeRef.current) {
                                scrollToCurrentTimeRef.current();
                              } else {
                                console.warn('Scroll function not available yet');
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-span-12 md:col-span-4 flex flex-col min-h-0">
                  <TopicsSection
                    accountId={selectedAccount?.id}
                    onTopicSelect={handleTopicSelect}
                    mediaFilters={mediaFilters}
                    onMediaFilterChange={handleMediaFilterChange}
                    selectedAccount={selectedAccount}
                    selectedPost={selectedPost}
                    onRefetchReady={setTopicsRefetch}
                    clickBehavior="details"
                  />
                </div>
                <div className="col-span-12 md:col-span-4 flex flex-col min-h-0">
                  <ActiveProjectsSection accountId={selectedAccount?.id} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PostForm Modal */}
        {showPostFormModal && (
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-50 p-4"
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleClosePostFormModal();
              }
            }}
          >
            <div
              className="bg-cmp-raise rounded-cmp3 shadow-cmp-e3 border border-cmp-line max-w-4xl w-full flex flex-col"
              style={{
                maxHeight: '90vh',
                height: 'auto',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="overflow-y-auto"
                style={{
                  flex: '1 1 auto',
                  minHeight: 0,
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                <SimplifiedPostForm
                  selectedPlatforms={selectedPlatforms}
                  selectedProfiles={selectedProfiles}
                  selectedAccount={selectedAccount}
                  selectedPost={selectedPost}
                  setSelectedPost={setSelectedPost}
                  socialProfilesData={socialProfilesData}
                  editMode={editMode}
                  setEditMode={setEditMode}
                  formData={formData}
                  setFormData={setFormData}
                  selectedFormProfiles={selectedFormProfiles}
                  setSelectedFormProfiles={setSelectedFormProfiles}
                  onClose={handleClosePostFormModal}
                  accountId={selectedAccount?.id}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Onboarding Modal */}
      {showOnboarding && (
        <OnboardingModal
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
          initialStep={onboardingInitialStep}
          initialAccountId={onboardingAccountId}
          initialAccountPlan={selectedAccountPlan}
        />
      )}
    </>
  );
}
